import { useMemo, useRef, useState } from "react";

import { Button } from "./button";
import { Input } from "./input";

export function AutoComplete({
  id,
  options,
  value,
  label,
  placeholder,
  dropdown,
  disabled,
  clearButton,
  onChange,
}: {
  id?: string;
  options: string[];
  value?: string;
  label?: string;
  placeholder?: string;
  dropdown?: boolean;
  disabled?: boolean;
  clearButton?: boolean;
  onChange?: (value: string) => void;
}) {
  const [selectedIndex, setSelectedIndex] = useState<number>(-1);
  const [open, setOpen] = useState<boolean>(false);
  const [useFilter, setUseFilter] = useState<boolean>(false);

  const ref = useRef<HTMLInputElement>(null);
  const allRef = useRef<HTMLDivElement>(null);

  const filteredOptions = useFilter
    ? options.filter((option) =>
        option.toLowerCase().includes((value ?? "").toLowerCase()),
      )
    : options;

  const isOpen = useMemo(() => {
    return (
      open &&
      filteredOptions.length > 0 &&
      (filteredOptions[0] !== value || !useFilter)
    );
  }, [open, filteredOptions, value, useFilter]);

  // Register outside click event to close the dropdown
  useMemo(() => {
    function handleClickOutside(event: MouseEvent) {
      if (allRef.current && !allRef.current.contains(event.target as Node)) {
        setOpen(false);
        setSelectedIndex(-1);
      }
    }

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div ref={allRef} className="flex w-full">
      <div className="flex-grow relative">
        <Input
          ref={ref}
          clearButton={clearButton}
          disabled={disabled}
          id={id}
          inputClassName={dropdown ? "rounded-r-none" : ""}
          label={label}
          placeholder={placeholder}
          suffix={
            dropdown && (
              <Button
                className="!rounded-l-none w-[42px]"
                disabled={disabled}
                onClick={() => {
                  setUseFilter(false);
                  setOpen(true);
                  ref.current?.focus();
                }}
              >
                ▼
              </Button>
            )
          }
          suffixNoStyle={true}
          type="text"
          value={value}
          onChange={(event) => {
            setUseFilter(true);
            onChange?.(event.target.value ?? "");
          }}
          onFocus={() => {
            if (value && value !== "") {
              setOpen(true);
            }
          }}
          onKeyDown={(event) => {
            if (event.key === "ArrowDown") {
              if (!open) {
                setOpen(true);
              }
              setSelectedIndex((prevIndex) =>
                Math.min(prevIndex + 1, filteredOptions.length - 1),
              );
            } else if (event.key === "ArrowUp") {
              setSelectedIndex((prevIndex) => Math.max(0, prevIndex - 1));
            } else if (event.key === "Enter") {
              event.preventDefault();
              const option = filteredOptions[selectedIndex];
              if (option) {
                setOpen(false);
                onChange?.(option);
              }
            }
          }}
        />
        {isOpen && filteredOptions.length > 0 && (
          <div className="bg-white absolute z-10 bg-white border border-gray-300 w-[calc(100%-43px)] max-h-60 overflow-y-auto flex flex-col">
            {filteredOptions.map((option, index) => (
              <button
                key={index}
                className={`text-left px-2 py-1 ${index === selectedIndex ? "bg-gray-200" : ""}`}
                type="button"
                onClick={() => {
                  onChange?.(option);
                  setOpen(false);
                }}
              >
                {option}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
